import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EventInput } from "@fullcalendar/core";
import {
    CalendarRRuleOptions,
    createNewEventData,
    createSimplyDailyEventWidthSchedulable,
    findSimplyDailyEventWithSchedulable,
    initEvent,
    sanitizeDailyTime,
    sanitizeEventUpdate,
    scheduleEventToCalendarEvent,
    updateTimeOnTheSameDate,
} from "./eventsUtils";
import { Schedule } from "userful-chronos-app-common-js/dist/models/schedule/schedule";
import {
    SchedulableEvent,
    SchedulableEventAndScheduleCreator,
} from "userful-chronos-app-common-js/dist/models/schedule/event";
import { requestCreateAndScheduleEvent, requestRemoveEvent, requestRemovePassiveMapper } from "./msgs/MsgSender";
import {
    SchedulableID,
    SchedulableType,
    SHUTDOWN_SCHEDULABLE,
} from "userful-chronos-app-common-js/dist/models/schedule/schedulable";
import { NOT_SET_VALUE, StringID } from "userful-chronos-app-common-js/dist/models/common";
import { ConfirmationToastContentStore } from "../../Container/ConfirmationToastContentStore";
import { toast } from "react-toastify";
import { requestRemoveAlertConfigSched } from "../notifications/messages/messageSender";
import { requestRemoveUCAction } from "../displays/msgs/UClientActionsMsgSender";
import { requestRemoveLEDAction } from "../integration/msgs/LEDActionsMsgSender";
import { requestRemoveMapperLayoutMapping } from "userful-chronos-app-common-js/dist/models/layouts/messages/MapperLayoutMsgSender";

const initSchedulable: SchedulableID = {
    value: NOT_SET_VALUE,
    type: "MAPPER",
};

export interface EventsState {
    scheduleEvents: SchedulableEvent[];
    schedules: Schedule[];
    currentEvent: EventInput | null;
    isCreate: boolean;
    selectedSchedulable: SchedulableID;
    pendingAddEvents: {
        removeEvent: StringID;
        addRequest: SchedulableEventAndScheduleCreator;
    }[];
    expectedConfirmationCreate: string | null;
    expectedConfirmationUpdate: string | null;
}

const initialState: EventsState = {
    scheduleEvents: [],
    schedules: [],
    currentEvent: null,
    isCreate: false,
    selectedSchedulable: initSchedulable,
    pendingAddEvents: [],
    expectedConfirmationCreate: null,
    expectedConfirmationUpdate: null,
};

export const eventsSlice = createSlice({
    name: "eventsSlice",
    initialState,
    reducers: {
        setEvents: (state, action: PayloadAction<SchedulableEvent[]>) => {
            state.scheduleEvents = [...action.payload];
        },
        setSchedules: (state, action: PayloadAction<Schedule[]>) => {
            state.schedules = [...action.payload];
        },
        notifyEventAdded: (state, action: PayloadAction<SchedulableEvent>) => {
            const foundIndex = state.scheduleEvents.findIndex((item) => item.id.value === action.payload.id.value);
            if (foundIndex < 0) {
                state.scheduleEvents = [...state.scheduleEvents, { ...action.payload }];
            }
            if (state.expectedConfirmationCreate !== null) {
                const customDisplay = ConfirmationToastContentStore(action.payload.name, "Save");
                toast(customDisplay, { containerId: "confirmationContainer" });
                state.expectedConfirmationCreate = null;
            }
        },
        notifyScheduleAdded: (state, action: PayloadAction<Schedule>) => {
            state.schedules = [...state.schedules, { ...action.payload }];
        },
        notifyEventDeleted: (state, action: PayloadAction<SchedulableEvent>) => {
            state.scheduleEvents = state.scheduleEvents.filter((item) => item.id.value !== action.payload.id.value);
        },
        onRespondEventRemoved: (state, action: PayloadAction<StringID>) => {
            const pendingRequests = state.pendingAddEvents.filter(
                (item) => item.removeEvent.value === action.payload.value,
            );
            state.pendingAddEvents = state.pendingAddEvents.filter(
                (item) => item.removeEvent.value !== action.payload.value,
            );
            for (const pendingRequest of pendingRequests) {
                requestCreateAndScheduleEvent(pendingRequest.addRequest);
            }
        },
        notifyScheduleDeleted: (state, action: PayloadAction<Schedule>) => {
            state.schedules = state.schedules.filter((item) => item.id.value !== action.payload.id.value);
        },
        notifyEventUpdated: (state, action: PayloadAction<SchedulableEvent>) => {
            const foundIndex = state.scheduleEvents.findIndex((item) => item.id.value === action.payload.id.value);
            if (foundIndex >= 0) {
                state.scheduleEvents = [
                    ...state.scheduleEvents.slice(0, foundIndex),
                    { ...action.payload },
                    ...state.scheduleEvents.slice(foundIndex + 1),
                ];
                if (state.expectedConfirmationUpdate !== null) {
                    const customDisplay = ConfirmationToastContentStore(action.payload.name, "Edit");
                    toast(customDisplay, { containerId: "confirmationContainer" });
                    state.expectedConfirmationUpdate = null;
                }
            }
        },
        notifyScheduleUpdated: (state, action: PayloadAction<Schedule>) => {
            const foundIndex = state.schedules.findIndex((item) => item.id.value === action.payload.id.value);
            if (foundIndex >= 0) {
                state.schedules = [
                    ...state.schedules.slice(0, foundIndex),
                    { ...action.payload },
                    ...state.schedules.slice(foundIndex + 1),
                ];
            }
        },

        updateEventTime: (
            state,
            action: PayloadAction<{
                id: string;
                start: string;
                end: string;
                allowedEventType: SchedulableType[];
            }>,
        ) => {
            const foundEvent = state.scheduleEvents.find((item) => item.id.value === action.payload.id);
            const foundCalendarEvent = scheduleEventToCalendarEvent(
                foundEvent,
                state.schedules,
                action.payload.allowedEventType,
            );
            const updateCalendarEvent = initEvent(action.payload.start, action.payload.end);
            if (
                foundCalendarEvent &&
                foundEvent.schedulableID &&
                action.payload.allowedEventType.includes(foundEvent.schedulableID.type)
            ) {
                const existingRrule = foundCalendarEvent.rrule as CalendarRRuleOptions;
                const update: EventInput = {
                    ...foundCalendarEvent,
                    start: updateCalendarEvent.start,
                    end: updateCalendarEvent.end,
                    duration: updateCalendarEvent.duration,
                    rrule: {
                        ...existingRrule,
                        dtstart:
                            existingRrule && existingRrule.dtstart
                                ? updateTimeOnTheSameDate(existingRrule.dtstart as string, action.payload.start)
                                : action.payload.start,
                    },
                };
                state.pendingAddEvents = [
                    ...state.pendingAddEvents,
                    {
                        removeEvent: { value: action.payload.id },
                        addRequest: createNewEventData(update, foundEvent.schedulableID, existingRrule.tzid),
                    },
                ];
                requestRemoveEvent({ value: action.payload.id });
            }
        },

        openEventEdit: (
            state,
            action: PayloadAction<{
                id: string;
                allowedEventType: SchedulableType[];
            }>,
        ) => {
            const foundEvent = state.scheduleEvents.find((item) => item.id.value === action.payload.id);
            const foundCalendarEvent = scheduleEventToCalendarEvent(
                foundEvent,
                state.schedules,
                action.payload.allowedEventType,
            );
            if (
                foundCalendarEvent &&
                foundEvent.schedulableID &&
                action.payload.allowedEventType.includes(foundEvent.schedulableID.type)
            ) {
                state.currentEvent = { ...foundCalendarEvent };
                state.isCreate = false;
                state.selectedSchedulable = foundEvent.schedulableID;
            }
        },
        openEventCreate: (
            state,
            action: PayloadAction<{
                start: string;
                end: string;
                defaultEventType: SchedulableType;
            }>,
        ) => {
            state.currentEvent = { ...initEvent(action.payload.start, action.payload.end) };
            state.selectedSchedulable = {
                value: NOT_SET_VALUE,
                type: action.payload.defaultEventType,
            };
            state.isCreate = true;
        },
        closeEventForm: (state) => {
            state.currentEvent = null;
            state.selectedSchedulable = initSchedulable;
        },
        updateCurrentEventSettings: (state, action: PayloadAction<Partial<EventInput>>) => {
            state.currentEvent = sanitizeEventUpdate(state.currentEvent, action.payload);
        },
        saveEvent: (state, action: PayloadAction<SchedulableID>) => {
            if (!state.currentEvent) {
                return;
            }
            if (!state.isCreate) {
                state.expectedConfirmationUpdate = state.currentEvent.id;
                requestRemoveEvent({ value: state.currentEvent.id });
                state.pendingAddEvents = [
                    ...state.pendingAddEvents,
                    {
                        removeEvent: { value: state.currentEvent.id },
                        addRequest: createNewEventData(state.currentEvent, action.payload || state.selectedSchedulable),
                    },
                ];
            } else {
                state.expectedConfirmationCreate = state.currentEvent.id;
                requestCreateAndScheduleEvent(
                    createNewEventData(state.currentEvent, action.payload || state.selectedSchedulable),
                );
            }
            state.selectedSchedulable = initSchedulable;
        },
        saveEventMulti: (
            state,
            action: PayloadAction<
                {
                    schedulableID: SchedulableID;
                    timeZone: string;
                }[]
            >,
        ) => {
            if (!state.currentEvent) {
                return;
            }
            if (!state.isCreate) {
                state.expectedConfirmationUpdate = state.currentEvent.id;
                requestRemoveEvent({ value: state.currentEvent.id });
                state.pendingAddEvents = [
                    ...state.pendingAddEvents,
                    ...action.payload.map((item) => ({
                        removeEvent: { value: state.currentEvent.id },
                        addRequest: createNewEventData(
                            state.currentEvent,
                            item.schedulableID || state.selectedSchedulable,
                            item.timeZone,
                        ),
                    })),
                ];
            } else {
                state.expectedConfirmationCreate = state.currentEvent.id;
                for (const item of action.payload) {
                    requestCreateAndScheduleEvent(
                        createNewEventData(
                            state.currentEvent,
                            item.schedulableID || state.selectedSchedulable,
                            item.timeZone,
                        ),
                    );
                }
                state.currentEvent = null;
            }
            state.selectedSchedulable = initSchedulable;
        },
        deleteEvent: (state) => {
            if (!state.currentEvent) {
                return;
            }
            const foundEvent = state.scheduleEvents.find((item) => item.id.value === state.currentEvent.id);
            if (!foundEvent) {
                return;
            }
            requestRemoveEvent(foundEvent.id);
            if (state.selectedSchedulable.value !== NOT_SET_VALUE && state.selectedSchedulable.type === "MAPPER") {
                requestRemovePassiveMapper({
                    value: state.selectedSchedulable.value,
                });
            }
            if (state.selectedSchedulable.value !== NOT_SET_VALUE && state.selectedSchedulable.type === "LAYOUT") {
                requestRemoveMapperLayoutMapping({
                    value: state.selectedSchedulable.value,
                });
            }
            if (state.selectedSchedulable.value !== NOT_SET_VALUE && state.selectedSchedulable.type === "ALERT") {
                requestRemoveAlertConfigSched({
                    value: state.selectedSchedulable.value,
                });
            }
            if (state.selectedSchedulable.value !== NOT_SET_VALUE && state.selectedSchedulable.type === "UCLIENT") {
                requestRemoveUCAction({
                    value: state.selectedSchedulable.value,
                });
            }
            if (
                state.selectedSchedulable.value !== NOT_SET_VALUE &&
                state.selectedSchedulable.type === "LED_CONTROLLER"
            ) {
                requestRemoveLEDAction({
                    value: state.selectedSchedulable.value,
                });
            }
            // not needed. linked schedule will be automaticaly deleted
            // const foundSchedule = state.schedules.find(item => item.id.value === foundEvent.scheduleID.value);
            // if (foundSchedule) {
            //     requestRemoveSchedule(foundSchedule.id);
            // }
            state.currentEvent = null;
            state.selectedSchedulable = initSchedulable;
        },

        requestAddOrUpdateShutdownEvent: (state, action: PayloadAction<string>) => {
            const time = sanitizeDailyTime(new Date(action.payload));

            const foundData = findSimplyDailyEventWithSchedulable(
                state.scheduleEvents,
                state.schedules,
                SHUTDOWN_SCHEDULABLE,
            );
            if (foundData) {
                requestRemoveEvent(foundData.event.id);
            }
            const createData = createSimplyDailyEventWidthSchedulable(SHUTDOWN_SCHEDULABLE, time);
            requestCreateAndScheduleEvent(createData);
        },

        requestRemoveShutdownEvent: (state) => {
            const foundData = findSimplyDailyEventWithSchedulable(
                state.scheduleEvents,
                state.schedules,
                SHUTDOWN_SCHEDULABLE,
            );
            if (foundData) {
                requestRemoveEvent(foundData.event.id);
            }
        },
    },
});

export const eventsActions = eventsSlice.actions;

export default eventsSlice.reducer;
